import React, { useEffect, useRef } from 'react';

import { PageLayout } from 'common/components';

import { staticStyles } from './style';

const DOLPHIN_TRADE_HUB_URL =
  'https://dolmarkets.tradingbull.ai/?token=1270%7Cer4MH2jcFamgt0uJVv28dbEjXoMM7qNE5yKP3ZrM4c436731&widget_id=6059328&theme=light';
const WIDGET_ORIGIN = new URL(DOLPHIN_TRADE_HUB_URL).origin;

// Apple 设备（iPhone/iPad/Mac）不展示 Trade Hub（iOS App 审核中），改为提示页
const IS_APPLE = /iPhone|iPad|iPod|Mac/.test(navigator.userAgent) || /iPad|Mac/.test(navigator.platform);

// 高度策略不变：iframe 恒 100vh 内部滚动（外部页 iframeResize 上报不可信，不监听，实测见知识库）。
// 以下实现 tradingbull widget 的宿主消息协议（其官方宿主示例）：widget 依赖父页提供的可视区域
// 信息（parentScroll）来渲染并居中全屏弹窗遮罩，宿主不实现会出现遮罩只盖住部分区域的问题。
// 注意 CRM 的滚动容器是 body 而非 documentElement，隐藏外层滚动条需操作 body。
const DolphinTradeHubPage = () => {
  const iframeRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    let savedScrollY = 0;

    // 收紧 PageLayout 顶部间距：给最近的 .PageLayout 祖先加 class
    const pageLayout = document.querySelector('.DolphinTradeHubPage')?.closest('.PageLayout');
    if (pageLayout) {
      pageLayout.classList.add('DolphinTradeHubPage--compact');
    }

    const sendScrollToIframe = () => {
      if (!iframe.contentWindow) return;
      iframe.contentWindow.postMessage(
        {
          type: 'parentScroll',
          scrollY: window.scrollY,
          viewportHeight: window.innerHeight,
          // iframe 顶部在父文档中的偏移，widget 用它换算可视区在 iframe 内的位置
          iframeTop: iframe.getBoundingClientRect().top + window.scrollY,
        },
        WIDGET_ORIGIN
      );
    };

    const handleMessage = event => {
      if (event.origin !== WIDGET_ORIGIN || !event.data) return;
      switch (event.data.type) {
        case 'requestParentScroll':
          sendScrollToIframe();
          break;
        case 'iframeResize': {
          // 动态适配：上报高度 < 600px 时用上报值（Disclaimer 等短内容），否则用 100vh（主面板）
          const reported = event.data.height;
          if (reported && reported < 600) {
            iframe.style.height = `${reported}px`;
          } else {
            iframe.style.height = '100vh';
          }
          break;
        }
        case 'fullScreenModalOpen':
          iframe.style.height = '100vh';
          savedScrollY = window.scrollY;
          window.scrollTo({ top: 0, behavior: 'instant' });
          sendScrollToIframe();
          break;
        case 'fullScreenModalClosed':
          iframe.style.height = '';
          window.scrollTo({ top: savedScrollY, behavior: 'instant' });
          savedScrollY = 0;
          break;
        case 'hideParentScrollbar':
          document.body.style.overflow = 'hidden';
          break;
        case 'restoreParentScrollbar':
          document.body.style.overflow = '';
          break;
        default:
          break;
      }
    };

    window.addEventListener('scroll', sendScrollToIframe, { passive: true });
    window.addEventListener('resize', sendScrollToIframe);
    window.addEventListener('message', handleMessage);
    iframe.addEventListener('load', sendScrollToIframe);
    sendScrollToIframe();

    return () => {
      window.removeEventListener('scroll', sendScrollToIframe);
      window.removeEventListener('resize', sendScrollToIframe);
      window.removeEventListener('message', handleMessage);
      document.body.style.overflow = '';
      pageLayout?.classList.remove('DolphinTradeHubPage--compact');
    };
  }, []);

  if (IS_APPLE) {
    return (
      <PageLayout captionSlug="dolphinTradeHub">
        <div className="DolphinTradeHubPage">
          <div className="DolphinTradeHubPage__iosNotice">
            <div className="DolphinTradeHubPage__hourglass">
              <img src="/image/hourglass.png" alt="" />
            </div>
            <h2>IOS App Coming Soon</h2>
            <p>The IOS version is currently under review and pending release.</p>
            <p className="DolphinTradeHubPage__iosNoticeLine2">Please stay tuned for further updates.</p>
            <div className="DolphinTradeHubPage__iosNoticeIcon">
              <img src="/image/app-store-icon.svg" alt="App Store" />
            </div>
            <a className="DolphinTradeHubPage__iosNoticeButton" href="/">
              Go back to main page
            </a>
          </div>
        </div>
        <style jsx global>
          {staticStyles}
        </style>
      </PageLayout>
    );
  }

  return (
    <PageLayout captionSlug="dolphinTradeHub">
      <div className="DolphinTradeHubPage">
        <iframe
          ref={iframeRef}
          src={DOLPHIN_TRADE_HUB_URL}
          title="Dolphin Trade Hub"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
      <style jsx global>
        {staticStyles}
      </style>
    </PageLayout>
  );
};

export default DolphinTradeHubPage;
